/**
 * Selection for an infinitely-scrolling mail list.
 *
 * **Selection is exactly what is ticked.** The header checkbox selects the
 * LOADED rows; scrolling more into view does not add them; every bulk action
 * acts on precisely those ids.
 *
 * ## The escalation this used to offer, and why it is gone (#710)
 *
 * Both inboxes scroll infinitely, so "select all" is ambiguous in a way a
 * paged list never was — and the first answer to that was a second selection
 * MODE: ticking the header offered *"Select all 248 in Inbox →"*, which
 * switched the selection to a predicate evaluated server-side.
 *
 * It did not survive contact with the rest of the surface. The destructive
 * verbs (Delete, Restore, purge) act on materialised ids and cannot expand a
 * predicate, so under an escalated selection the bar still offered Delete and
 * the handlers refused it — *"Select loaded messages before using this
 * action"*. The bar advertised an action that was guaranteed to fail.
 *
 * QA and product chose the simpler contract over more guards: don't offer the
 * escalation at all. That removes a mode, a banner, a capability flag
 * (`allowEscalation`, which existed because the personal lane's `sender`
 * filter has no predicate equivalent), and every downstream branch that had to
 * ask which mode was active.
 *
 * **The server's predicate API is untouched** — `MailPredicate` in
 * `services/mail_state.py` is what makes a future "select all matching" cheap
 * to reintroduce, and it is exercised by its own tests. What is gone is the
 * client's ability to enter that mode, and with it every client-side branch
 * that existed only to survive it.
 */
import { useCallback, useMemo, useState } from 'react';

/**
 * The only selection mode there is.
 *
 * Kept as a named export rather than deleted outright: it is what
 * `buildTarget` produces and what the endpoints call this shape, and a name is
 * cheaper to read at a call site than a bare string. `SELECTION_PREDICATE` was
 * removed with the escalation — see the module docstring.
 */
export const SELECTION_IDS = 'ids';

export default function useMailSelection({ entries = [], total = 0 }) {
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    const loadedIds = useMemo(
        () => entries.map((e) => e.comm_id).filter(Boolean),
        [entries],
    );

    const clear = useCallback(() => setSelectedIds(new Set()), []);

    const toggleOne = useCallback((commId, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(commId);
            else next.delete(commId);
            return next;
        });
    }, []);

    const toggleAllLoaded = useCallback(
        (checked) => setSelectedIds(checked ? new Set(loadedIds) : new Set()),
        [loadedIds],
    );

    const allLoadedSelected =
        loadedIds.length > 0 && loadedIds.every((id) => selectedIds.has(id));
    const someSelected = selectedIds.size > 0 && !allLoadedSelected;

    /**
     * The selected rows, for the summaries below.
     *
     * Selected ids that are no longer loaded (the user scrolled, a refetch
     * dropped a row) simply do not appear here. The summaries are about which
     * DIRECTION a verb should point, and a row nobody can see should not get a
     * vote in that.
     */
    const selectedRows = useMemo(
        () => entries.filter((entry) => selectedIds.has(entry.comm_id)),
        [entries, selectedIds],
    );

    /**
     * Whether any selected row is still unread — the bulk bar's read direction.
     *
     * The bar offers exactly one read action, so it has to know which way to
     * point without re-deriving selection state somewhere else. A row with an
     * ABSENT `is_read` counts as read (the backend's tri-state, and MailRow's),
     * so historical rows cannot make an all-read selection look mixed.
     */
    const hasUnread = useMemo(
        () => selectedRows.some((entry) => entry.is_read === false),
        [selectedRows],
    );

    /**
     * Whether any selected row is archived — the bulk bar's archive direction.
     *
     * The mirror of `hasUnread`, and it exists for a defect of exactly the same
     * shape (#710): in **All mail** an archived row's own hover strip said
     * *Move to Inbox* while the bulk bar said *Archive*, which is a no-op on a
     * row that is already archived. The row strip asked the row; the bar asked
     * a static per-folder table that had no entry for `all` and fell back to
     * the Inbox template.
     *
     * `archived_at` is the discriminator, matching `mailRowState.mailStateToggles`
     * — one definition of "archived" for the row strip and the bar.
     */
    const hasArchived = useMemo(
        () => selectedRows.some((entry) => Boolean(entry.archived_at)),
        [selectedRows],
    );

    /**
     * The `target` every mail-state endpoint accepts.
     *
     * Always `ids` now. The API still accepts a `predicate` and refuses
     * both-empty with a 422 rather than treating it as a no-op, because a bulk
     * action that silently matched nothing is indistinguishable from one that
     * silently matched everything.
     */
    const buildTarget = useCallback(
        (inboxId = null) => ({
            ...(inboxId ? { inbox_id: inboxId } : {}),
            ids: Array.from(selectedIds),
        }),
        [selectedIds],
    );

    return {
        selectedIds,
        selectedCount: selectedIds.size,
        mode: SELECTION_IDS,
        allLoadedSelected,
        someSelected,
        /** True when any selected row has `is_read: false`. */
        hasUnread,
        /** True when any selected row carries an `archived_at`. */
        hasArchived,
        loadedCount: loadedIds.length,
        total,
        toggleOne,
        toggleAllLoaded,
        clear,
        buildTarget,
    };
}
